import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Store, Plus, Printer, Settings2 } from "lucide-react";

const DEFAULT_PRINTER = { paper_size: "58", logo_url: "", header_text: "", footer_text: "Terima kasih", auto_print_order: false, auto_print_receipt: true };

export default function OutletsPage() {
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [printerOpen, setPrinterOpen] = useState(null);
  const [form, setForm] = useState({ name: "", brand: "", address: "", phone: "" });
  const [pf, setPf] = useState(DEFAULT_PRINTER);

  const load = () => api.get("/outlets").then(({data})=>setOutlets(data));
  useEffect(() => { load(); }, []);

  function startEdit(outlet) {
    setEditId(outlet.id);
    setForm({
      name: outlet.name || "",
      brand: outlet.brand || "",
      address: outlet.address || "",
      phone: outlet.phone || "",
    });
    setOpen(true);
  }

  async function saveOutlet() {
    const payload = {
      name: form.name.trim(),
      brand: form.brand.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
    };
    if (!payload.name || !payload.brand) {
      toast.error("Nama & brand wajib");
      return;
    }
    try {
      if (editId) {
        await api.put(`/outlets/${editId}`, payload);
        toast.success("Outlet diperbarui");
      } else {
        await api.post("/outlets", payload);
        toast.success("Outlet ditambahkan");
      }
      setOpen(false);
      setEditId(null);
      setForm({ name: "", brand: "", address: "", phone: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  }

  function openPrinter(outlet) {
    setPf({ ...DEFAULT_PRINTER, ...(outlet.printer_config || {}) });
    setPrinterOpen(outlet);
  }

  async function savePrinter() {
    try {
      await api.put(`/outlets/${printerOpen.id}`, { printer_config: pf });
      toast.success("Printer profile disimpan");
      setPrinterOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div data-workspace-redundant-header><h1 className="text-2xl font-extrabold font-display">Outlet</h1></div>
          <p className="text-slate-500 text-sm">{outlets.length} cabang MAZBOY GROUP · Klik <Printer className="w-3 h-3 inline"/> untuk atur printer per outlet</p>
        </div>
        <Button data-testid="outlet-add-btn" onClick={()=>{setEditId(null);setForm({name:"",brand:"",address:"",phone:""});setOpen(true);}} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah Outlet</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {outlets.map(o => (
          <Card key={o.id} className="hover:shadow-md transition">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><Store className="w-5 h-5"/></div>
                <div className="flex items-center gap-1">
                  <Button data-testid={`outlet-edit-${o.id}`} onClick={()=>startEdit(o)} size="sm" variant="outline" className="h-7 px-2">Edit</Button>
                  <Button data-testid={`outlet-printer-${o.id}`} onClick={()=>openPrinter(o)} size="sm" variant="outline" className="h-7 px-2"><Printer className="w-3.5 h-3.5"/></Button>
                  <Badge variant="outline">{String(o.status || "—").replace(/_/g, " ").toUpperCase()}</Badge>
                </div>
              </div>
              <CardTitle className="text-base mt-2">{o.name}</CardTitle>
              <div className="text-xs text-slate-500">{o.brand}</div>
              <div className="text-xs font-mono break-all text-slate-500 flex items-start gap-2">
                <span className="min-w-0">{o.id}</span>
                <button type="button" className="text-blue-600 hover:underline shrink-0"
                  title="Salin ID Outlet"
                  onClick={() => { navigator.clipboard.writeText(o.id).then(() => toast.success("ID Outlet disalin")).catch(() => toast.error("Gagal menyalin ID")); }}>
                  Salin ID
                </button>
              </div>
            </CardHeader>
            <CardContent className="text-sm space-y-1 pt-0">
              <div className="text-slate-600">{o.address || "—"}</div>
              <div className="text-slate-600 font-mono text-xs">{o.phone || "—"}</div>
              {o.printer_config && (
                <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs text-slate-500">
                  <Printer className="w-3 h-3"/>
                  <span>{o.printer_config.paper_size || "58"}mm</span>
                  {o.printer_config.auto_print_order && <Badge variant="outline" className="text-[10px]">Auto Order</Badge>}
                  {o.printer_config.auto_print_receipt && <Badge variant="outline" className="text-[10px]">Auto Struk</Badge>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(value)=>{setOpen(value);if(!value)setEditId(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Outlet" : "Tambah Outlet"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama Outlet</Label><Input data-testid="outlet-form-name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})}/></div>
            <div><Label>Brand</Label><Input data-testid="outlet-form-brand" value={form.brand} onChange={e=>setForm({...form, brand: e.target.value})}/></div>
            <div><Label>Alamat</Label><Input value={form.address} onChange={e=>setForm({...form, address: e.target.value})}/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button data-testid="outlet-form-submit" onClick={saveOutlet} className="bg-orange-500 hover:bg-orange-600">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!printerOpen} onOpenChange={(o)=>!o && setPrinterOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4"/>Printer Profile · {printerOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ukuran Kertas</Label>
              <Select value={pf.paper_size} onValueChange={v=>setPf({...pf, paper_size:v})}>
                <SelectTrigger data-testid="printer-paper"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="58">58mm (Thermal kecil)</SelectItem>
                  <SelectItem value="80">80mm (Thermal besar)</SelectItem>
                  <SelectItem value="a4">A4 (Printer biasa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>URL Logo (opsional)</Label><Input data-testid="printer-logo" value={pf.logo_url} onChange={e=>setPf({...pf, logo_url:e.target.value})} placeholder="https://..."/></div>
            <div><Label>Header Tambahan</Label><Input data-testid="printer-header" value={pf.header_text} onChange={e=>setPf({...pf, header_text:e.target.value})} placeholder="Contoh: NPWP / Slogan"/></div>
            <div><Label>Footer</Label><Input data-testid="printer-footer" value={pf.footer_text} onChange={e=>setPf({...pf, footer_text:e.target.value})} placeholder="Contoh: Terima kasih, sampai jumpa"/></div>
            <div className="flex items-center justify-between border-t pt-3">
              <div>
                <Label className="text-sm">Auto Print Order (konfirmasi pesanan)</Label>
                <p className="text-xs text-slate-500">Cetak otomatis sebelum pembayaran</p>
              </div>
              <Switch data-testid="printer-auto-order" checked={pf.auto_print_order} onCheckedChange={v=>setPf({...pf, auto_print_order:v})}/>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Auto Print Struk Pembayaran</Label>
                <p className="text-xs text-slate-500">Cetak otomatis setelah pembayaran sukses</p>
              </div>
              <Switch data-testid="printer-auto-receipt" checked={pf.auto_print_receipt} onCheckedChange={v=>setPf({...pf, auto_print_receipt:v})}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPrinterOpen(null)}>Batal</Button>
            <Button data-testid="printer-save" onClick={savePrinter} className="bg-orange-500 hover:bg-orange-600">Simpan Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
